import React, { useState } from 'react';
import type { SecretaryUser } from '../../../shared/api/secretaryApi';

interface HeaderProps {
  user: SecretaryUser;
  onLogout: () => void;
  currentView?: 'booking' | 'appointments';
  onNavigate?: (view: 'booking' | 'appointments') => void;
}

const Header: React.FC<HeaderProps> = ({ user, onLogout, currentView, onNavigate }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const getUserProfile = () => {
    const token = localStorage.getItem('secretaryGoogleToken');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return {
          name: payload.name || '',
          email: payload.email || '',
          picture: payload.picture || '',
        };
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  const userProfile = getUserProfile();

  const handleNavigate = (view: 'booking' | 'appointments') => {
    if (onNavigate) {
      onNavigate(view);
      setIsMenuOpen(false);
    }
  };

  return (
    <header className="bg-white shadow-md border-b border-slate-200 fixed top-0 right-0 left-0 z-40">
      <div className="px-4 lg:px-6 py-3 lg:py-4">
        <div className="flex items-center justify-between">
          {/* Right Side: Logo, Brand, and Mobile Menu Button */}
          <div className="flex items-center gap-2 lg:gap-4">
            {/* Mobile Menu Button - Visible only on mobile */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <svg className="w-6 h-6 text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>

            {/* Logo */}
            <div className="relative group">
              <div className="w-10 h-10 lg:w-12 lg:h-12 bg-gradient-to-br from-teal-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg transform group-hover:scale-105 transition-transform">
                <svg className="w-5 h-5 lg:w-7 lg:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4 0 00-6.364 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
            </div>
            
            {/* Brand Name - Now visible on all screen sizes */}
            <div>
              <h1 className="text-sm sm:text-base lg:text-xl font-bold text-slate-800 bg-gradient-to-r from-teal-600 to-blue-600 bg-clip-text text-transparent">
                מרכז מונא הרפואי
              </h1>
              <p className="text-xs lg:text-sm text-slate-600 font-medium">{user.clinicName}</p>
            </div>
          </div>

          {/* Left Side: Desktop - User Profile and Logout */}
          <div className="hidden md:flex items-center gap-2 lg:gap-4">
            <div className="text-right">
              <p className="text-sm font-semibold text-slate-800">{userProfile?.name || user.name}</p>
              <p className="text-xs text-slate-500">{userProfile?.email || user.email}</p>
            </div>
            
            {/* User Avatar */}
            <div className="relative group">
              {userProfile?.picture ? (
                <img 
                  src={userProfile.picture} 
                  alt={userProfile.name}
                  className="w-9 h-9 lg:w-11 lg:h-11 rounded-full border-2 border-teal-500 shadow-md"
                />
              ) : (
                <div className="w-9 h-9 lg:w-11 lg:h-11 rounded-full bg-gradient-to-br from-teal-400 to-blue-500 flex items-center justify-center text-white font-bold text-base lg:text-lg shadow-md">
                  {(userProfile?.name || user.name || 'U').charAt(0).toUpperCase()}
                </div>
              )}
            </div>

            {/* Logout Button */}
            <button
              onClick={onLogout}
              className="flex items-center gap-1 lg:gap-2 px-2 lg:px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 border border-red-200 hover:border-red-300"
            >
              <svg className="w-4 h-4 lg:w-5 lg:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span className="font-semibold text-sm lg:text-base hidden lg:inline">התנתק</span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Dropdown Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-white border-t border-slate-200 shadow-lg">
          <div className="p-4 space-y-3">
            {/* User Info with Logout Button */}
            <div className="flex items-center justify-between gap-3 pb-3 border-b border-slate-200">
              <div className="flex items-center gap-3 flex-1">
                {userProfile?.picture ? (
                  <img 
                    src={userProfile.picture} 
                    alt={userProfile.name}
                    className="w-12 h-12 rounded-full border-2 border-teal-500"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-400 to-blue-500 flex items-center justify-center text-white font-bold text-lg">
                    {(userProfile?.name || user.name || 'U').charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 text-right">
                  <p className="text-sm font-semibold text-slate-800">{userProfile?.name || user.name}</p>
                  <p className="text-xs text-slate-500">{userProfile?.email || user.email}</p>
                </div>
              </div>
              
              {/* Logout Button beside user info */}
              <button
                onClick={() => {
                  setIsMenuOpen(false);
                  onLogout();
                }}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-all border border-red-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span className="font-semibold text-sm">התנתק</span>
              </button>
            </div>

            {/* Navigation Buttons */}
            <button
              onClick={() => handleNavigate('appointments')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                currentView === 'appointments'
                  ? 'bg-gradient-to-r from-teal-500 to-blue-600 text-white shadow-lg'
                  : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <span className="font-semibold">התורים שלי</span>
            </button>

            <button
              onClick={() => handleNavigate('booking')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                currentView === 'booking'
                  ? 'bg-gradient-to-r from-teal-500 to-blue-600 text-white shadow-lg'
                  : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="font-semibold">תור חדש</span>
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
